import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const filterChips = ["ALL", "STRATEGY", "CULTURE", "INSIGHTS", "WELLBEING", "ACCESSIBILITY"];
const coverPalettes = [{
  bg: "bg-secondary",
  text: "text-secondary-foreground",
  rule: "bg-primary",
  accent: "text-primary"
}, {
  bg: "bg-primary",
  text: "text-primary-foreground",
  rule: "bg-secondary",
  accent: "text-secondary"
}, {
  bg: "bg-accent",
  text: "text-accent-foreground",
  rule: "bg-secondary",
  accent: "text-secondary"
}, {
  bg: "bg-secondary",
  text: "text-secondary-foreground",
  rule: "bg-primary",
  accent: "text-primary"
}, {
  bg: "bg-primary",
  text: "text-primary-foreground",
  rule: "bg-secondary",
  accent: "text-secondary"
}, {
  bg: "bg-accent",
  text: "text-accent-foreground",
  rule: "bg-secondary",
  accent: "text-secondary"
}];
function stripHtml(html?: string): string {
  if (!html) return "";
  return html.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
}
function formatDate(dateStr?: string): string {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit"
    }).toUpperCase();
  } catch {
    return "";
  }
}
function estimateReadingTime(content?: string, excerpt?: string): string {
  const text = stripHtml(content) || stripHtml(excerpt) || "";
  const words = text.split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(3, Math.ceil(words / 200));
  return `${minutes} MIN READ`;
}
function RecentChaptersGrid() {
  const {
    posts,
    loading,
    error,
    refetch,
    query
  } = usePosts();
  console.log("WP_Query Parameters:", query);
  console.log("Posts Loading State:", loading);
  console.log("Fetched Posts Array:", posts);
  if (loading) {
    return <div data-section-id="1156" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="aspect-[3/4] bg-muted border border-border animate-pulse rounded-lg" />)}
      </div>;
  }
  if (error) {
    return <div className="border border-border bg-card p-10 text-center rounded-lg shadow-md">
        <p className="font-default italic text-foreground/80 mb-4">
          Our knowledge base is temporarily unavailable.
        </p>
        <Button onClick={refetch} variant="outline" className="font-mono uppercase tracking-widest text-xs rounded-lg border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground">
          Try Again
        </Button>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="border border-border bg-card p-16 text-center rounded-lg shadow-md">
        <p className="font-mono uppercase tracking-widest text-xs text-destructive mb-3">
          § ARCHIVE — STATUS EMPTY
        </p>
        <p className="font-default italic text-lg text-foreground/80">
          No insights have been published yet. Check back soon.
        </p>
      </div>;
  }
  console.log("Final Rendered Posts Count:", posts.length);
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
      {posts.map((post, i) => {
      const palette = coverPalettes[i % coverPalettes.length];
      const category = post.categories && post.categories.length > 0 ? post.categories[0].name : "INSIGHT";
      const indexLabel = `FG.${String(i + 1).padStart(3, "0")}${String.fromCharCode(97 + i % 3)}`;
      const editionNo = `Vol. ${String(i + 1).padStart(2, "0")}`;
      const cleanTitle = stripHtml(post.title);
      return <article data-index={i} key={post.id} className="group bg-card border border-border rounded-lg overflow-hidden transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-xl">
            <Link to={post.link || "#"} className="block">
              <div className={`relative aspect-[3/4] ${palette.bg} ${palette.text} overflow-hidden transition-transform duration-300 ease-out group-hover:scale-[1.02]`}>
                <div className="absolute inset-0 opacity-[0.15] pointer-events-none mix-blend-overlay" style={{
              backgroundImage: "radial-gradient(circle at 20% 30%, white 0%, transparent 2%), radial-gradient(circle at 70% 60%, white 0%, transparent 1.5%), radial-gradient(circle at 40% 80%, white 0%, transparent 2%)"
            }} />

                <div className="absolute top-6 left-6 right-6 flex items-start justify-between">
                  <span className={`font-mono text-[0.7rem] uppercase tracking-widest ${palette.accent}`}>
                    § {indexLabel}
                  </span>
                  <span className={`font-mono text-[0.65rem] uppercase tracking-widest ${palette.text} opacity-70`}>
                    {category}
                  </span>
                </div>

                <div className="absolute inset-x-6 top-1/2 -translate-y-1/2">
                  <h3 className="font-default font-bold leading-tight tracking-tight" style={{
                fontSize: "clamp(1.5rem, 2.4vw, 2.125rem)"
              }}>
                    {cleanTitle}
                  </h3>
                  <div className="font-default text-sm text-muted-foreground mt-3 leading-relaxed">
                    {stripHtml(post.excerpt)}
                  </div>
                  <div className="mt-6 flex items-center">
                    <span className={`h-1 w-16 ${palette.rule} transition-all duration-300 ease-out group-hover:w-32 rounded-full`} />
                    <span className={`ml-2 h-2 w-2 rounded-full ${palette.rule}`} />
                  </div>
                </div>

                <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                  <span className={`font-mono text-[0.65rem] uppercase tracking-widest ${palette.text} opacity-80`}>
                    Feel Good Works
                  </span>
                  <span className={`font-mono text-[0.65rem] uppercase tracking-widest ${palette.accent}`}>
                    {editionNo}
                  </span>
                </div>
              </div>

              <div className="px-5 py-4 bg-card border-t border-border flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3 font-mono text-[0.7rem] uppercase tracking-widest text-muted-foreground">
                  <span>{formatDate(post.date)}</span>
                  <span className="h-1 w-1 rounded-lg bg-primary" />
                  <span>{category}</span>
                </div>
                <span className="font-mono text-[0.7rem] uppercase tracking-widest text-muted-foreground">
                  {estimateReadingTime(post.content, post.excerpt)}
                </span>
              </div>
            </Link>
          </article>;
    })}
    </div>;
}
export default function LatestArticlesGrid() {
  const wp_query_params = {
    post_type: "post",
    per_page: 100,
    order: "desc",
    orderby: "date",
    status: ["publish"]
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="latest-articles-grid" className="relative bg-background text-foreground py-28 md:py-40 overflow-hidden">
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
      backgroundImage: "radial-gradient(circle at 12% 18%, var(--primary) 0%, transparent 1.5%), radial-gradient(circle at 78% 32%, var(--secondary) 0%, transparent 1.2%), radial-gradient(circle at 35% 70%, var(--ring) 0%, transparent 1.8%), radial-gradient(circle at 88% 85%, var(--primary) 0%, transparent 1.4%), radial-gradient(circle at 50% 50%, var(--secondary) 0%, transparent 1%)"
    }} />

      <div className="relative max-w-[1320px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mb-16 md:mb-24">
          

          <div className="lg:col-span-5 lg:pt-4">
            
            
          </div>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 100,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <RecentChaptersGrid />
        </PostsProvider>
      </div>
    </section>;
}